/**
 * Risks Tab Component
 *
 * Displays comprehensive risks list with server-side:
 * - Pagination (25 items per page default)
 * - Search (debounced 500ms, searches vendor/issue description)
 * - Filtering (severity: Critical, High, Medium, Low)
 * - Sorting (vendor name, reporting date, severity)
 *
 * Features:
 * - Vendor logos with fallback rendering
 * - Severity chips with color coding
 * - References tooltips with clickable links
 * - Initial filter support from All Third-Parties tab
 */

import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { cn, Select, SelectItem, type SortDescriptor } from "@nextui-org/react";
import { useCallback, useEffect } from "react";
import { RisksColumns } from "../constants/risksColumns";
import { useRisksServerSideState } from "../hooks/useServerSideState";
import { RisksFilterState } from "../index";
import { useGetRisks } from "../requests/getRisks";
import type { RiskItem } from "../types/risks";

interface RisksTabProps {
  initialFilter?: RisksFilterState | null;
  onFilterApplied?: () => void;
}

export function RisksTab({ initialFilter, onFilterApplied }: RisksTabProps) {
  // Server-side state management
  const { queryParams, pagination, search, sort, severity } = useRisksServerSideState({
    page: 1,
    page_size: 25,
    sort_by: 'reporting_date',
    sort_order: 'desc',
  });

  // Fetch risks from API
  const { data, isLoading, isFetching, isError, error } = useGetRisks(queryParams);

  // Extract data with defaults
  const risks = data?.risks ?? [];
  const totalIssues = data?.total_issues ?? 0;
  const totalPages = data?.total_pages ?? 0;
  const currentPage = data?.page ?? 1;

  // Apply initial filter when navigating from All Third-Parties tab
  useEffect(() => {
    if (initialFilter) {
      search.setSearchTerm(initialFilter.vendorName);

      // Map severity string to typed value
      const severityValue = initialFilter.severity === 'all'
        ? undefined
        : (initialFilter.severity as 'Critical' | 'High' | 'Medium' | 'Low');

      severity.setValue(severityValue);

      // Notify parent that filter has been applied
      onFilterApplied?.();
    }
  }, [initialFilter, onFilterApplied, search, severity]);

  // Handle sort changes from table
  const handleSortModelChange = useCallback((sortDescriptor: SortDescriptor) => {
    const column = String(sortDescriptor.column);

    if (!column) {
      sort.resetSort();
      return;
    }

    // Map column names to API sort fields
    const columnToFieldMap: Record<string, string> = {
      third_party_product: 'vendor_name',
      reporting_date: 'reporting_date',
      severity: 'severity',
    };

    const sortBy = columnToFieldMap[column];

    if (sortBy) {
      // Cast to any because useServerSort is typed for vendors but we're using it for risks
      // setSort handles toggle logic internally
      sort.setSort(sortBy as any);
    }
  }, [sort]);

  return (
    <div className="h-full w-full flex flex-col gap-4 overflow-hidden mb-4">
      {/* Header: Title + Count | Severity Filter + Search */}
      <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
        {/* Left: Title + Count */}
        <div className="flex flex-row items-center gap-2 flex-grow">
          <h2 className="font-['Inter'] font-medium text-[14px] leading-[20px] text-[#11181C]">
            Risks
          </h2>
          {!isLoading && !isFetching && (
            <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-[#F4F4F5] rounded-[45px]">
              <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-[#52525B]">
                {totalIssues}
              </span>
            </div>
          )}
        </div>

        {/* Right: Severity Filter + Search */}
        <div className="flex flex-row items-center gap-2">
          <Select
            placeholder="All Severities"
            selectedKeys={[severity.value ?? 'all']}
            onChange={(e) => {
              const value = e.target.value;
              severity.setValue(
                value === 'all'
                  ? undefined
                  : (value as 'Critical' | 'High' | 'Medium' | 'Low')
              );
            }}
            className="w-[160px]"
            classNames={{
              trigger: "h-[36px] bg-white border-1 border-[#E4E4E7] rounded-[6px]",
              value: "font-['Inter'] font-normal text-[14px] leading-[20px] text-[#3F3F46]",
            }}
          >
            <SelectItem key="all" value="all">
              All Severities
            </SelectItem>
            <SelectItem key="Critical" value="Critical">
              Critical
            </SelectItem>
            <SelectItem key="High" value="High">
              High
            </SelectItem>
            <SelectItem key="Medium" value="Medium">
              Medium
            </SelectItem>
            <SelectItem key="Low" value="Low">
              Low
            </SelectItem>
          </Select>

          <SearchField
            value={search.searchTerm}
            onChange={search.setSearchTerm}
            placeholder="Search by name"
            className="w-[420px]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="relative flex-1 min-h-0">
          {/* Loading overlay - only during refetch, not initial load */}
          {!isLoading && isFetching && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
              <LoadingSpinner />
            </div>
          )}
          <ZTableDeprecrated<RiskItem>
          className={cn(
            'border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto flex-1'
          )}
          layout="auto"
          loading={isLoading || isFetching}
          rows={risks}
          columns={RisksColumns}
          isHeaderSticky
          isCompact
          removeWrapper
          getRowId={(row) => row.id}
          tableHeaderProps={{ className: 'shadow-none rounded-none' }}
          tableRowProps={(row, rowIndex) => ({
            className: cn(rowIndex === 0 ? 'border-none' : 'border-y', 'group'),
          })}
          classNames={{
            wrapper: '!shadow-none !rounded-none',
          }}
          aria-label="Risks List"
          onSortModelChange={handleSortModelChange}
          tableBodyProps={{
            emptyContent: isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner
                  containerClassName="bg-transparent relative"
                  className="mx-auto"
                />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-red-500 text-sm font-medium">
                  Failed to load risks
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {error?.message ?? 'An unexpected error occurred'}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-gray-500 text-sm">No risks found</p>
              </div>
            ),
          }}
          />
        </div>
        <ZTablePagination
          totalPages={totalPages}
          paginationState={{
            currentPage: currentPage,
            rowsPerPage: pagination.pageSize,
          }}
          setPagination={{
            handlePageChange: (page) => pagination.setPage(page),
            handleRowsPerPageChange: (rows) => pagination.setPageSize(rows),
          }}
          isDisabled={isLoading || isFetching}
        />
      </div>
    </div>
  );
}
